import { useState, useCallback, useRef } from 'react';

const STORAGE_KEY = 'tradrin-ai-config';
const API_BASE = window.location.hostname === 'localhost'
  ? 'http://localhost:8000/api'
  : '/api';

const DEFAULT_MODELS = {
  claude: 'claude-sonnet-4-6',
  openai: 'gpt-4o',
  deepseek: 'deepseek-chat',
  mistral: 'mistral-large-latest',
  grok: 'grok-3-mini',
  custom: '',
};

const PROVIDER_MODELS = {
  claude: ['claude-sonnet-4-6', 'claude-opus-4-6', 'claude-haiku-4-5-20251001', 'claude-3-5-sonnet-20241022', 'claude-3-5-haiku-20241022'],
  openai: ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo', 'o1', 'o1-mini', 'o3-mini'],
  deepseek: ['deepseek-chat', 'deepseek-reasoner'],
  mistral: ['mistral-large-latest', 'mistral-small-latest', 'codestral-latest'],
  grok: ['grok-3', 'grok-3-mini'],
  custom: [],
};

function loadConfig() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

function saveConfig(config) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
}

function buildChartContext(visibleCandles, indicators, positions, session) {
  if (!visibleCandles?.length) return {};

  const last50 = visibleCandles.slice(-50).map(c => ({
    time: c.time, open: c.open, high: c.high, low: c.low, close: c.close, volume: c.volume,
  }));

  // Extract latest indicator values
  const indicatorValues = {};
  if (indicators) {
    indicators.forEach(ind => {
      if (ind.data?.length > 0) {
        const last = ind.data[ind.data.length - 1];
        indicatorValues[ind.label || ind.id] = last?.value ?? last;
      }
    });
  }

  const pos = positions?.map(p => ({
    direction: p.direction,
    entry: p.entry_price,
    sl: p.stop_loss,
    tp: p.take_profit,
    volume: p.volume,
    pnl: p.unrealized_pnl,
  })) || [];

  return {
    instrument: session?.instrument || '',
    timeframe: session?.timeframe || '',
    current_price: last50[last50.length - 1]?.close,
    candles: last50,
    indicators: indicatorValues,
    positions: pos,
  };
}

export default function useAIAnalysis(visibleCandles, activeIndicators, openPositions, session) {
  const saved = loadConfig();
  const [provider, setProvider] = useState(saved?.provider || 'claude');
  const [model, setModel] = useState(saved?.model || DEFAULT_MODELS.claude);
  const [apiKey, setApiKey] = useState(saved?.apiKey || '');
  const [baseUrl, setBaseUrl] = useState(saved?.baseUrl || '');
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const updateProvider = useCallback((newProvider) => {
    setProvider(newProvider);
    setModel(DEFAULT_MODELS[newProvider] || '');
  }, []);

  const saveSettings = useCallback(() => {
    saveConfig({ provider, model, apiKey, baseUrl });
  }, [provider, model, apiKey, baseUrl]);

  const clearChat = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  const stopStreaming = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setStreaming(false);
  }, []);

  const sendMessage = useCallback(async (userText) => {
    if (!userText.trim() || !apiKey || !model) return;

    setError(null);
    const userMsg = { role: 'user', content: userText };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);

    // Save settings on each send
    saveConfig({ provider, model, apiKey, baseUrl });

    const chartContext = buildChartContext(visibleCandles, activeIndicators, openPositions, session);
    const abortController = new AbortController();
    abortRef.current = abortController;

    setStreaming(true);
    // Add placeholder for assistant message
    const assistantIdx = newMessages.length;
    setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

    try {
      const token = localStorage.getItem('token');
      const body = {
        provider,
        model,
        api_key: apiKey,
        messages: newMessages.map(m => ({ role: m.role, content: m.content })),
        chart_context: chartContext,
      };
      if (provider === 'custom' && baseUrl) {
        body.base_url = baseUrl;
      }

      const response = await fetch(`${API_BASE}/ai/analyze/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        },
        body: JSON.stringify(body),
        signal: abortController.signal,
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulated = '';

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'content') {
              accumulated += data.text;
              const snapshot = accumulated;
              setMessages(prev => {
                const updated = [...prev];
                updated[assistantIdx] = { role: 'assistant', content: snapshot };
                return updated;
              });
            } else if (data.type === 'error') {
              setError(data.text);
            }
          } catch { /* skip malformed SSE lines */ }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message || 'Connection failed.');
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  }, [messages, provider, model, apiKey, baseUrl, visibleCandles, activeIndicators, openPositions, session]);

  return {
    // Settings
    provider, model, apiKey, baseUrl,
    setProvider: updateProvider, setModel, setApiKey, setBaseUrl,
    saveSettings,
    // Chat
    messages, streaming, error,
    sendMessage, clearChat, stopStreaming,
    // Helpers
    isConfigured: !!(apiKey && model),
    DEFAULT_MODELS,
    PROVIDER_MODELS,
  };
}
